import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/useI18n';
import { projectApi } from '../api/client';

export default function EditProject() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pid = searchParams.get('pid');

  const [project, setProject] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [metaForm, setMetaForm] = useState({
    title: '',
    type: '0',
    performer_email: '',
  });

  useEffect(() => {
    if (!pid) {
      alert(t('paramError'));
      navigate('/');
      return;
    }
    initPage();
  }, [pid]);

  async function initPage() {
    try {
      // 获取工程信息
      const pRes = await projectApi.getList();
      if (pRes.success) {
        const proj = pRes.data.projects.find(p => p.project_id == pid);
        if (proj) {
          setProject(proj);
          setMetaForm({
            title: proj.title,
            type: proj.type.toString(),
            // performer_id = 0 表示之前被拒绝过，需要重新指定
            performer_email: proj.performer_id > 0 ? (proj.performer_name || '') : '',
          });
        }
      }

      // 获取预设
      const preRes = await projectApi.getPresets(pid);
      if (preRes.success) {
        const existingPresets = preRes.data.presets || [];
        if (existingPresets.length > 0) {
          setPresets(existingPresets);
        } else {
          setPresets([{ reason: '', points: '' }]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function addPresetRow() {
    setPresets([...presets, { reason: '', points: '' }]);
  }

  function removePresetRow(index) {
    setPresets(presets.filter((_, i) => i !== index));
  }

  function updatePresetRow(index, field, value) {
    const newPresets = [...presets];
    newPresets[index][field] = value;
    setPresets(newPresets);
  }

  async function saveMeta() {
    setSaving(true);
    try {
      const updateData = {
        project_id: pid,
        title: metaForm.title,
        type: parseInt(metaForm.type),
      };

      // 如果填写了执行者邮箱，则更新执行者
      const performerEmail = metaForm.performer_email.trim();
      if (performerEmail) {
        updateData.performer_email = performerEmail;
      }

      const res = await projectApi.update(updateData);
      if (res.success) {
        alert(t('basicConfigUpdated'));
        // 刷新工程信息
        initPage();
      } else {
        alert(res.message || t('configUpdateFailed'));
      }
    } catch (err) {
      alert(t('requestFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function savePresetsHandler() {
    setSaving(true);
    try {
      const cleanedPresets = presets
        .filter(p => p.reason.trim())
        .map(p => ({
          reason: p.reason.trim(),
          points: parseFloat(p.points) || 0,
        }));

      const res = await projectApi.savePresets(pid, cleanedPresets);
      if (res.success) {
        alert(t('presetsSynced'));
      } else {
        alert(res.message || t('savePresetFailed'));
      }
    } catch (err) {
      alert(t('requestFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function closeProject() {
    const confirmMsg = t('confirmCloseTitle') + '\n\n' + t('confirmCloseMsg');

    if (!confirm(confirmMsg)) return;
    if (!confirm(t('confirmCloseFinal'))) return;

    try {
      const res = await projectApi.close(pid);
      if (res.success) {
        alert(t('projectClosed'));
        navigate('/');
      } else {
        alert(res.message || t('actionFailed'));
      }
    } catch (err) {
      alert(t('networkFailed'));
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">{t('loading')}</div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-indigo-600 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex items-center">
          <button
            onClick={() => navigate('/')}
            className="mr-3 p-1 hover:bg-indigo-500 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <h1 className="text-lg font-bold">{t('editProjectTitle')}</h1>
        </div>
      </nav>

      <main className="container mx-auto p-4 max-w-md space-y-6">
        {/* 基础配置 */}
        <section className="bg-white rounded-2xl shadow-sm p-6 space-y-4 border border-gray-100">
          <h3 className="text-xs font-black text-indigo-500 uppercase tracking-widest">{t('basicConfig')}</h3>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('projectTitle')}</label>
            <input
              type="text"
              value={metaForm.title}
              onChange={(e) => setMetaForm({ ...metaForm, title: e.target.value })}
              className="w-full border-2 border-gray-50 bg-gray-50 rounded-xl p-3 outline-none focus:border-indigo-500 transition-all font-bold"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-2">{t('systemMode')}</label>
            <div className="grid grid-cols-2 gap-3">
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="proj_type"
                  value="0"
                  checked={metaForm.type === '0'}
                  onChange={(e) => setMetaForm({ ...metaForm, type: e.target.value })}
                  className="hidden peer"
                />
                <div className="p-3 text-center border-2 rounded-xl peer-checked:border-indigo-500 peer-checked:bg-indigo-50 text-gray-400 peer-checked:text-indigo-600 transition-all">
                  <span className="block text-sm font-bold">{t('positive')}</span>
                  <span className="text-[9px]">{t('incentiveMode')}</span>
                </div>
              </label>
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="proj_type"
                  value="1"
                  checked={metaForm.type === '1'}
                  onChange={(e) => setMetaForm({ ...metaForm, type: e.target.value })}
                  className="hidden peer"
                />
                <div className="p-3 text-center border-2 rounded-xl peer-checked:border-indigo-500 peer-checked:bg-indigo-50 text-gray-400 peer-checked:text-indigo-600 transition-all">
                  <span className="block text-sm font-bold">{t('negative')}</span>
                  <span className="text-[9px]">{t('taskMode')}</span>
                </div>
              </label>
            </div>
          </div>

          {/* 执行者 */}
          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">
              {t('performer')}
              {project?.performer_id == 0 && <span className="ml-1 text-amber-500">({t('reInvite') || '需要重新指定'})</span>}
            </label>
            <input
              type="email"
              value={metaForm.performer_email}
              onChange={(e) => setMetaForm({ ...metaForm, performer_email: e.target.value })}
              placeholder={t('performerEmailPlaceholder') || '输入执行者邮箱'}
              className="w-full border-2 border-gray-50 bg-gray-50 rounded-xl p-3 outline-none focus:border-indigo-500 transition-all"
            />
            {project?.performer_id > 0 && (
              <p className="text-[10px] text-gray-400 mt-1">{t('currentPerformer')}: {project.performer_name}</p>
            )}
          </div>

          <button
            onClick={saveMeta}
            disabled={saving}
            className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-indigo-100 active:scale-95 transition-all disabled:opacity-50"
          >
            {t('saveBasicConfig')}
          </button>
        </section>

        {/* 预设配置 */}
        <section className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-4">{t('presetConfig')}</h3>

          <div id="presets_list" className="space-y-3">
            {presets.map((preset, index) => (
              <div key={index} className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder={t('reason')}
                  value={preset.reason}
                  onChange={(e) => updatePresetRow(index, 'reason', e.target.value)}
                  className="pre-reason flex-[3] border-b-2 border-gray-50 p-2 text-sm outline-none focus:border-indigo-300"
                />
                <input
                  type="number"
                  placeholder={t('pointsHint')}
                  value={preset.points}
                  onChange={(e) => updatePresetRow(index, 'points', e.target.value)}
                  className="pre-points flex-1 border-b-2 border-gray-50 p-2 text-sm outline-none focus:border-indigo-300"
                />
                <button
                  onClick={() => removePresetRow(index)}
                  className="text-gray-300 hover:text-red-500 px-1"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addPresetRow}
            className="mt-4 w-full py-3 border-2 border-dashed border-gray-100 text-gray-300 rounded-xl text-sm font-bold hover:text-indigo-500 hover:border-indigo-200 transition-all"
          >
            {t('addPreset')}
          </button>

          <button
            onClick={savePresetsHandler}
            disabled={saving}
            className="mt-6 w-full bg-gray-900 text-white py-3 rounded-xl font-bold active:scale-95 transition-all disabled:opacity-50"
          >
            {t('syncPresets')}
          </button>
        </section>

        {/* 危险区域 */}
        <section className="mt-10 pt-6 border-t border-red-100">
          <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
            <h3 className="text-xs font-black text-red-500 uppercase tracking-widest mb-1">{t('dangerZone')}</h3>
            <p className="text-[10px] text-red-400 mb-4">
              {t('dangerZoneHint')}
            </p>

            <button
              onClick={closeProject}
              className="w-full bg-white text-red-600 border border-red-200 py-3 rounded-xl font-bold text-sm shadow-sm active:bg-red-600 active:text-white transition-all"
            >
              {t('closeProject')}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
